import { Auth } from 'aws-amplify';
import { isAfter } from 'date-fns';


export function createApiUtils<
    TApiConfigOptions extends ApiUtilsConfig,
    TFetchOptions extends FetchOptions
>({
    getAuthToken,
    getCustomHeaders,
    getRequestUrl
}: ApiUtilsOptions<TApiConfigOptions, TFetchOptions>) {
    let config: TApiConfigOptions | undefined;

    let apiConfigPromise: Promise<void> | null = null;
    let resolveApiConfigPromise: ((value: (void | PromiseLike<void>)) => void) | null = null;

    async function doFetch(
        method: string,
        url: string,
        body: any,
        options?: TFetchOptions
    ): Promise<any> {
        if (!config) {
            if (!apiConfigPromise) {
                apiConfigPromise = new Promise<void>(resolve => {
                    resolveApiConfigPromise = resolve;
                });
            }
        }

        if (apiConfigPromise) {
            await apiConfigPromise;
        }

        const Authorization = await getAuthToken(url, options);

        const fullUrl = getRequestUrl(url, options, config);

        const response: Response = await fetch(fullUrl, {
            method,
            headers: {
                Accept: 'application/json',
                Authorization,
                ...(!options?.useAutoContentType && {
                    'Content-Type': options?.contentType || 'application/json'
                }),
                ...(getCustomHeaders?.())
            },
            ...(!!body && {
                body: options?.useAutoContentType ? body : JSON.stringify(body)
            })
        });

        if (options?.emptyResponse) {
            if (!response.ok) {
                let json = undefined;

                try {
                    json = await response.json();
                } catch (e) {  }

                throw json || {
                    message: 'An unexpected error occurred. Please contact support.'
                };
            }

            return response;
        }

        const json = response.json();

        if (!response.ok) {
            throw await json;
        }

        return json;
    }

    return {
        configure(newConfig: TApiConfigOptions | undefined) {
            config = newConfig;

            if (resolveApiConfigPromise) {
                resolveApiConfigPromise();
                resolveApiConfigPromise = null;
            }
        },

        getConfig(): TApiConfigOptions | undefined {
            return config;
        },

        get<TGetParams extends GetParams | null>(url: string, params?: TGetParams, options?: TFetchOptions) {
            if (params) {
                const urlParams = new URLSearchParams(Object.fromEntries(
                    Object.entries(params).filter(([ _, value ]) => (
                        value !== null && value !== undefined
                    ))
                ));

                url = `${url}?${urlParams.toString()}`;
            }

            return doFetch('GET', url, null, options);
        },

        patch(url: string, body: any, options?: TFetchOptions) {
            return doFetch('PATCH', url, body, options);
        },

        post(url: string, body: any, options?: TFetchOptions) {
            return doFetch('POST', url, body, options);
        },

        put(url: string, body: any, options?: TFetchOptions) {
            return doFetch('PUT', url, body, options);
        },

        delete(url: string, options?: TFetchOptions) {
            return doFetch('DELETE', url, null, {
                emptyResponse: true,
                ...options
            } as TFetchOptions);
        }
    };
}

export const EXPIRATION_DATE_OFFSET = 10000;

export async function getCognitoToken() {
    const session = await Auth.currentSession();

    const exp = session.getAccessToken().getExpiration();
    const expiration = new Date(exp * 1000);

    if (expiration && isAfter(new Date(Date.now() + EXPIRATION_DATE_OFFSET), expiration)) {
        console.log('Refreshing expired authentication token');
        const user = await Auth.currentAuthenticatedUser();

        await new Promise<void>((resolve) => {
            user.refreshSession(session.getRefreshToken(), () => resolve());
        });
    }

    return `Bearer ${(await Auth.currentSession()).getAccessToken().getJwtToken()}`;
}

export function isApiError(error: unknown): error is ApiError {
    return error instanceof Object
        && error.hasOwnProperty('type')
        && error.hasOwnProperty('title')
        && error.hasOwnProperty('detail')
        && error.hasOwnProperty('status');
}

export function isValidationApiError(error: unknown): error is ValidationApiError {
    return error instanceof Object
        && error.hasOwnProperty('type')
        && error.hasOwnProperty('title')
        && error.hasOwnProperty('status')
        && error.hasOwnProperty('errors');
}

export interface ApiUtilsConfig {
    apiUrl: string;
}

interface ApiUtilsOptions<
    TApiConfigOptions extends ApiUtilsConfig,
    TFetchOptions extends FetchOptions
> {
    getAuthToken(url?: string, options?: TFetchOptions): Promise<string>;
    getCustomHeaders?: () => { [ key: string ]: string };
    getRequestUrl: (url: string, options: TFetchOptions | undefined, config: TApiConfigOptions | undefined) => string;
}

export interface FetchOptions {
    contentType?: string;
    emptyResponse?: boolean;
    useAutoContentType?: boolean;
}

interface ApiError {
    type: string;
    title: string;
    detail: string;
    status: number;
}

interface ValidationApiError extends ApiError {
    errors: {
        [fieldPath: string]: string[];
    };
}

interface GetParams {
    [key: string]: any;
}
